import type {
  EvergreenMp4PartUploadUrl,
  EvergreenMp4UploadCompleteInput,
  EvergreenMp4UploadInit,
  EvergreenMp4UploadInitInput,
  EvergreenMp4UploadPartsRequest,
  EvergreenMp4UploadStatusDto,
} from '@/features/webinar/types';
import { getBackErrorCodes } from '@/lib/evergreenInteractionSchedule';
import type { EvergreenMp4UploadError } from '@/lib/evergreenMp4File';

export const EVERGREEN_MP4_MIME_TYPE = 'video/mp4';
export const EVERGREEN_MP4_MAX_SIZE_BYTES = 10 * 1024 * 1024 * 1024;
export const PART_URL_BATCH_SIZE = 10;
const MAX_PART_URLS_PER_REQUEST = 100;
const DEFAULT_CONCURRENCY = 3;
const DEFAULT_MAX_ATTEMPTS = 3;
const RETRY_BASE_DELAY_MS = 1000;

export type UploadPart = { partNumber: number; start: number; end: number };

export type CompletedPart = { partNumber: number; eTag: string };

export class PartUploadError extends Error {
  readonly status: number | null;
  readonly retryable: boolean;

  constructor(message: string, status: number | null, retryable = true) {
    super(message);
    this.name = 'PartUploadError';
    this.status = status;
    this.retryable = retryable;
  }
}

export class UploadAbortedError extends Error {
  constructor() {
    super('Upload aborted');
    this.name = 'UploadAbortedError';
  }
}

export function planUploadParts(sizeBytes: number, partSizeBytes: number): UploadPart[] {
  if (sizeBytes <= 0 || partSizeBytes <= 0) return [];
  const parts: UploadPart[] = [];
  for (let start = 0, partNumber = 1; start < sizeBytes; start += partSizeBytes, partNumber++) {
    parts.push({ partNumber, start, end: Math.min(start + partSizeBytes, sizeBytes) });
  }
  return parts;
}

export function buildPartUrlsRequest(
  firstPartNumber: number,
  totalParts: number,
  batchSize = PART_URL_BATCH_SIZE,
): EvergreenMp4UploadPartsRequest {
  const remaining = totalParts - firstPartNumber + 1;
  return {
    firstPartNumber,
    partsCount: Math.max(1, Math.min(batchSize, MAX_PART_URLS_PER_REQUEST, remaining)),
  };
}

export type PutPart = (
  url: string,
  body: Blob,
  onProgress: (loadedBytes: number) => void,
  signal: AbortSignal,
) => Promise<string>;

export const putPartWithXhr: PutPart = (url, body, onProgress, signal) =>
  new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new UploadAbortedError());
      return;
    }
    const xhr = new XMLHttpRequest();
    const onAbort = () => xhr.abort();
    signal.addEventListener('abort', onAbort, { once: true });
    const cleanup = () => signal.removeEventListener('abort', onAbort);

    xhr.upload.onprogress = (event) => onProgress(event.loaded);
    xhr.onload = () => {
      cleanup();
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(new PartUploadError(`Part upload failed with status ${xhr.status}`, xhr.status));
        return;
      }
      const eTag = xhr.getResponseHeader('ETag');
      // Sans ETag exposé par le CORS du bucket, la finalisation est impossible : inutile de réessayer.
      if (!eTag) {
        reject(new PartUploadError('Missing ETag header', xhr.status, false));
        return;
      }
      resolve(eTag);
    };
    xhr.onerror = () => {
      cleanup();
      reject(new PartUploadError('Network error during part upload', null));
    };
    xhr.onabort = () => {
      cleanup();
      reject(new UploadAbortedError());
    };
    xhr.open('PUT', url);
    xhr.send(body);
  });

export type UploadFileInPartsOptions = {
  file: Blob;
  init: Pick<EvergreenMp4UploadInit, 'partSizeBytes' | 'parts'>;
  fetchPartUrls: (request: EvergreenMp4UploadPartsRequest) => Promise<EvergreenMp4PartUploadUrl[]>;
  onProgress?: (sentBytes: number, totalBytes: number) => void;
  signal: AbortSignal;
  putPart?: PutPart;
  concurrency?: number;
  maxAttempts?: number;
  wait?: (ms: number) => Promise<void>;
};

const defaultWait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

function isRetryable(error: unknown): boolean {
  if (error instanceof UploadAbortedError) return false;
  if (error instanceof PartUploadError) {
    if (!error.retryable) return false;
    return error.status === null || error.status >= 500 || error.status === 403;
  }
  return true;
}

export async function uploadFileInParts({
  file,
  init,
  fetchPartUrls,
  onProgress,
  signal,
  putPart = putPartWithXhr,
  concurrency = DEFAULT_CONCURRENCY,
  maxAttempts = DEFAULT_MAX_ATTEMPTS,
  wait = defaultWait,
}: UploadFileInPartsOptions): Promise<CompletedPart[]> {
  const parts = planUploadParts(file.size, init.partSizeBytes);
  const controller = new AbortController();
  const abortFromCaller = () => controller.abort();
  signal.addEventListener('abort', abortFromCaller, { once: true });
  // Un signal déjà annulé ne redéclenche jamais `abort` : sans ce test, l'envoi partirait quand même.
  if (signal.aborted) controller.abort();
  const urls = new Map<number, string>(init.parts.map((p) => [p.partNumber, p.uploadUrl]));
  const pendingBatches = new Set<{ first: number; last: number; promise: Promise<void> }>();
  const loadedByPart = new Map<number, number>();
  const completed: CompletedPart[] = [];

  const reportProgress = () => {
    let sent = 0;
    for (const loaded of loadedByPart.values()) sent += loaded;
    onProgress?.(Math.min(sent, file.size), file.size);
  };

  const loadBatch = (partNumber: number): Promise<void> => {
    for (const pending of pendingBatches) {
      if (partNumber >= pending.first && partNumber <= pending.last) return pending.promise;
    }
    const request = buildPartUrlsRequest(partNumber, parts.length);
    const batch = {
      first: request.firstPartNumber,
      last: request.firstPartNumber + request.partsCount - 1,
      promise: Promise.resolve(),
    };
    batch.promise = fetchPartUrls(request)
      .then((received) => {
        for (const p of received) urls.set(p.partNumber, p.uploadUrl);
      })
      .finally(() => pendingBatches.delete(batch));
    pendingBatches.add(batch);
    return batch.promise;
  };

  const resolveUrl = async (partNumber: number, forceRefresh: boolean): Promise<string> => {
    if (forceRefresh) urls.delete(partNumber);
    if (!urls.has(partNumber)) await loadBatch(partNumber);
    const url = urls.get(partNumber);
    if (!url) throw new PartUploadError(`No upload URL for part ${partNumber}`, null);
    return url;
  };

  const uploadPart = async (part: UploadPart) => {
    let lastError: unknown;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      if (controller.signal.aborted) throw new UploadAbortedError();
      try {
        const expired = lastError instanceof PartUploadError && lastError.status === 403;
        const url = await resolveUrl(part.partNumber, expired);
        const eTag = await putPart(
          url,
          file.slice(part.start, part.end),
          (loaded) => {
            loadedByPart.set(part.partNumber, loaded);
            reportProgress();
          },
          controller.signal,
        );
        loadedByPart.set(part.partNumber, part.end - part.start);
        reportProgress();
        completed.push({ partNumber: part.partNumber, eTag });
        return;
      } catch (error) {
        lastError = error;
        loadedByPart.set(part.partNumber, 0);
        reportProgress();
        if (!isRetryable(error) || attempt === maxAttempts) throw error;
        await wait(RETRY_BASE_DELAY_MS * attempt);
      }
    }
    throw lastError;
  };

  let nextIndex = 0;
  const worker = async () => {
    while (nextIndex < parts.length) {
      const part = parts[nextIndex++];
      await uploadPart(part);
    }
  };

  reportProgress();
  try {
    await Promise.all(Array.from({ length: Math.min(concurrency, parts.length) }, () => worker()));
  } catch (error) {
    controller.abort();
    throw signal.aborted ? new UploadAbortedError() : error;
  } finally {
    signal.removeEventListener('abort', abortFromCaller);
  }
  return completed.sort((a, b) => a.partNumber - b.partNumber);
}

export type EvergreenMp4UploadApi = {
  init: (input: EvergreenMp4UploadInitInput) => Promise<EvergreenMp4UploadInit>;
  getPartUrls: (
    uploadId: number,
    request: EvergreenMp4UploadPartsRequest,
  ) => Promise<EvergreenMp4PartUploadUrl[]>;
  complete: (uploadId: number, input: EvergreenMp4UploadCompleteInput) => Promise<void>;
  getStatus: (uploadId: number) => Promise<EvergreenMp4UploadStatusDto>;
};

export type EvergreenMp4UploadResult =
  | { kind: 'ready'; durationSeconds: number | null; videoResourceId: number | null }
  | { kind: 'pendingPromotion' }
  | { kind: 'error'; error: EvergreenMp4UploadError }
  | { kind: 'aborted' };

export type RunEvergreenMp4UploadOptions = {
  file: File;
  api: EvergreenMp4UploadApi;
  signal: AbortSignal;
  onProgress: (sentBytes: number) => void;
  onValidating: () => void;
  pollIntervalMs?: number;
  validationTimeoutMs?: number;
  now?: () => number;
  putPart?: PutPart;
  wait?: (ms: number) => Promise<void>;
};

const STATUS_POLL_INTERVAL_MS = 5000;
const VALIDATION_TIMEOUT_MS = 20 * 60 * 1000;
const UPLOAD_API_ERRORS: Readonly<Record<string, EvergreenMp4UploadError>> = {
  'HTTP.ERROR.403.EVERGREEN_SESSION_IN_PROGRESS': 'EVERGREEN_SESSION_IN_PROGRESS',
  'HTTP.ERROR.400.EVERGREEN.MP4.CUTS.INVALID': 'REPLACEMENT_REJECTED',
  'HTTP.ERROR.400.EVERGREEN.MP4.CUTS.REQUIRED': 'REPLACEMENT_REJECTED',
  'HTTP.ERROR.400.EVERGREEN.MP4.CUTS.UNEXPECTED': 'REPLACEMENT_REJECTED',
  'HTTP.ERROR.400.EVERGREEN.MP4.CUTS.OVERLAP': 'REPLACEMENT_REJECTED',
  'HTTP.ERROR.400.EVERGREEN.MP4.CUTS.NOT_ALLOWED': 'REPLACEMENT_REJECTED',
  'HTTP.ERROR.400.EVERGREEN.MP4.CUTS.OUTSIDE_VIDEO': 'REPLACEMENT_REJECTED',
};

/** Le code est lu à la racine comme dans le détail d'une erreur de validation. */
export function toUploadError(error: unknown): EvergreenMp4UploadError {
  for (const code of getBackErrorCodes(error)) {
    const known = UPLOAD_API_ERRORS[code];
    if (known) return known;
  }
  return 'UPLOAD_FAILED';
}

export async function runEvergreenMp4Upload({
  file,
  api,
  signal,
  onProgress,
  onValidating,
  pollIntervalMs = STATUS_POLL_INTERVAL_MS,
  validationTimeoutMs = VALIDATION_TIMEOUT_MS,
  now = Date.now,
  putPart,
  wait = defaultWait,
}: RunEvergreenMp4UploadOptions): Promise<EvergreenMp4UploadResult> {
  let uploadId: number;
  try {
    const init = await api.init({
      fileName: file.name,
      mimeType: EVERGREEN_MP4_MIME_TYPE,
      sizeBytes: file.size,
    });
    uploadId = init.uploadId;
    const parts = await uploadFileInParts({
      file,
      init,
      signal,
      putPart,
      wait,
      fetchPartUrls: (request) => api.getPartUrls(uploadId, request),
      onProgress: (sentBytes) => onProgress(sentBytes),
    });
    if (signal.aborted) return { kind: 'aborted' };
    await api.complete(uploadId, { parts });
  } catch (error) {
    if (signal.aborted || error instanceof UploadAbortedError) return { kind: 'aborted' };
    return { kind: 'error', error: toUploadError(error) };
  }

  onValidating();

  const deadline = now() + validationTimeoutMs;
  while (now() < deadline) {
    await wait(pollIntervalMs);
    if (signal.aborted) return { kind: 'aborted' };
    let status: EvergreenMp4UploadStatusDto;
    try {
      status = await api.getStatus(uploadId);
    } catch {
      continue;
    }
    if (status.status === 'READY')
      return {
        kind: 'ready',
        durationSeconds: status.durationSeconds,
        videoResourceId: status.videoResourceId,
      };
    if (status.status === 'PENDING_PROMOTION') return { kind: 'pendingPromotion' };
    if (status.status === 'FAILED') {
      return { kind: 'error', error: status.failureCode ?? 'INTERNAL_ERROR' };
    }
    if (status.status === 'ABORTED') return { kind: 'error', error: 'ABORTED' };
  }
  return { kind: 'error', error: 'VALIDATION_TIMEOUT' };
}
